import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlusCircle, FiCode, FiMail, FiCopy } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { createEmailTemplate } from "../api/contacts";

// Helper function to resolve Spintax {Hi|Hello|Hey}
function resolveSpintaxPreview(text) {
  if (!text) return "";
  let result = text;
  const pattern = /\{([^{}]+)\}/g;
  let matches = listMatches(pattern, result);
  let attempts = 0;

  while (matches.length > 0 && attempts < 10) {
    attempts++;
    let replaced = false;
    result = result.replace(/\{([^{}]+)\}/g, (match, p1) => {
      if (p1.includes('|')) {
        replaced = true;
        const options = p1.split('|');
        return options[0]; // pick first option for stable live preview
      }
      return match;
    });
    if (!replaced) break;
  }
  return result;
}

function listMatches(pattern, str) {
  const matches = [];
  let m;
  while ((m = pattern.exec(str)) !== null) {
    if (m[1] && m[1].includes('|')) matches.push(m);
  }
  return matches;
}

// Helper function to replace placeholders with sample data
function previewWithSampleData(text) {
  const sampleData = {
    name: "John Smith",
    company_name: "Oris Dental Center",
    email: "odental657@gmail.com",
    phone: "043809990",
    category: "Dentists",
    area: "Jumeirah",
    address: "Umm Sequeim 3, Jumeirah Road, Opposite Burj Al Arab",
    website: "https://www.orisdentalcenter.ae/",
    services_offered: "Dental Care, Cosmetic Dentistry, Orthodontics",
  };

  if (!text) return "";

  let result = resolveSpintaxPreview(text);
  Object.keys(sampleData).forEach((key) => {
    const regex = new RegExp(`\\{${key}\\}`, "g");
    result = result.replace(regex, sampleData[key]);
  });

  return result;
}

const PRESET_TEMPLATES = [
  {
    id: "quick_question",
    name: "🔥 Quick Question (25%+ Reply Rate)",
    subject: "Quick question regarding {company_name}",
    body: `Hi {name},\n\nI came across {company_name} while researching top {category} companies in {area}.\n\nI had a quick idea on how you could get 5-10 extra client inquiries every week without spending extra on ads.\n\nWould you be open to a short 2-minute chat or should I send a 60-second video explaining the idea?\n\nBest regards,\nHamza`
  },
  {
    id: "pain_point",
    name: "🎯 Case Study & Results (High Conversion)",
    subject: "Idea for {company_name}'s client growth",
    body: `Hi {name},\n\nI was looking into {company_name} and noticed your work in {services_offered}.\n\nWe recently helped a similar business in your industry increase their qualified client bookings by 35% in under 30 days.\n\nI put together 3 specific suggestions tailored for {company_name}. Are you open for a quick 5-minute call this week to go over them?\n\nBest regards,\nHamza`
  },
  {
    id: "soft_touch",
    name: "⚡ 1-Sentence Soft Pitch (Busy Founders)",
    subject: "1 quick question for {name}",
    body: `Hi {name},\n\nAre you currently open to taking on more new clients for {company_name}?\n\nIf so, I'd love to share how we can deliver 10+ verified leads directly to your inbox each week.\n\nLet me know if you'd like me to send over a quick 1-page overview.\n\nBest regards,\nHamza`
  },
  {
    id: "compliment",
    name: "🌟 Compliment & Value Pitch",
    subject: "Impressed by {company_name}",
    body: `Hi {name},\n\nI was researching {category} providers in {area} and was really impressed by {company_name}.\n\nWe specialize in helping companies like yours build automated, predictable client acquisition systems.\n\nWould you be open to a quick chat this Thursday to see if we might be a fit?\n\nThanks,\nHamza`
  },
  {
    id: "follow_up",
    name: "📩 Gentle Follow-Up (Doubles Response Rate)",
    subject: "Re: Quick question regarding {company_name}",
    body: `Hi {name},\n\nFollowing up on my previous email. I know you're super busy managing {company_name}.\n\nJust wanted to check if acquiring more clients is a priority for you right now?\n\nIf not, no worries at all!\n\nBest regards,\nHamza`
  }
];

export default function CreateTemplate() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("raw"); // 'raw' or 'preview'

  const handleSelectPreset = (e) => {
    const presetId = e.target.value;
    if (!presetId) return;
    const selected = PRESET_TEMPLATES.find((p) => p.id === presetId);
    if (selected) {
      setName(selected.name.replace(/^[^\w]+/, "").trim());
      setSubject(selected.subject);
      setBody(selected.body);
      toast.success("Loaded preset template!");
    }
  };


  // Available placeholders
  const placeholders = [
    { key: "name", label: "Contact Name" },
    { key: "company_name", label: "Company Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "category", label: "Category" },
    { key: "area", label: "Area" },
    { key: "address", label: "Address" },
    { key: "website", label: "Website" },
    { key: "services_offered", label: "Services Offered" },
  ];

  const insertPlaceholder = (placeholder) => {
    setBody((prev) => prev + `{${placeholder}}`);
    toast.success(`Inserted {${placeholder}}`);
  };

  const copyPlaceholder = (placeholder) => {
    navigator.clipboard.writeText(`{${placeholder}}`);
    toast.success(`Copied {${placeholder}}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token?.access) return;

    setLoading(true);
    try {
      // use your API helper here
      await createEmailTemplate(token.access, name, subject, body);

      toast.success("Template created successfully!");
      navigate("/dashboard/templates/list");
    } catch (err) {
      console.error(err.message);
      toast.error("Error creating template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FiPlusCircle className="text-gray-700" /> Create Email Template
        </h2>
        <p className="text-gray-600 mt-1">
          Create a new email template with dynamic placeholders
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <div className="space-y-6">
          {/* Template Form */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Template Details
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Preset Selector */}
              <div className="bg-blue-50/70 border border-blue-200 p-4 rounded-lg">
                <label className="block text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
                  🚀 Load Proven High-Converting Cold Email Template
                </label>
                <select
                  onChange={handleSelectPreset}
                  defaultValue=""
                  className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>-- Select High-Converting Outreach Template --</option>
                  {PRESET_TEMPLATES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-blue-700 mt-1">
                  Selecting a preset will auto-fill high-converting cold email text below!
                </p>
              </div>

              {/* Template Name */}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g., Get Max Clients"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Subject Line */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  placeholder="e.g., Helping {company_name} get more clients"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Email Body */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Body
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                  rows="8"
                  placeholder="Hi {name},&#10;&#10;I noticed your business..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition font-mono text-sm"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? "Creating..." : "Create Template"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/templates/list")}
                  className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          {/* Available Placeholders */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Available Placeholders
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Click to insert into body or copy to clipboard
            </p>
            <div className="grid grid-cols-2 gap-2">
              {placeholders.map((placeholder) => (
                <div
                  key={placeholder.key}
                  className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg p-2 hover:bg-gray-100 transition"
                >
                  <button
                    type="button"
                    onClick={() => insertPlaceholder(placeholder.key)}
                    className="flex-1 text-left"
                  >
                    <div className="text-xs text-gray-500 font-medium">
                      {placeholder.label}
                    </div>
                    <code className="text-xs text-blue-700 font-mono">
                      {`{${placeholder.key}}`}
                    </code>
                  </button>
                  <button
                    type="button"
                    onClick={() => copyPlaceholder(placeholder.key)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 transition"
                    title="Copy"
                  >
                    <FiCopy size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Spintax Tip Card */}
            <div className="mt-4 bg-amber-50/80 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1">
              <div className="font-semibold flex items-center gap-1.5 text-amber-900 text-sm">
                <span>💡</span>
                <span>Avoid Spam Filters with Spintax</span>
              </div>
              <p className="text-amber-800 leading-relaxed">
                Wrap variations in curly braces with <code>|</code> separators so each lead gets a unique email:
              </p>
              <div className="bg-white border border-amber-200 rounded p-2 font-mono text-[11px] text-amber-950">
                {`{Hi|Hello|Hey}`} {`{name}`}, {`{I noticed|I saw}`} your company {`{company_name}`}...
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {/* Preview Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Live Preview
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                See how your template will look
              </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-white">
              <div className="flex px-6">
                <button
                  onClick={() => setActiveTab("raw")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "raw"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <FiCode size={16} />
                  Raw Template
                </button>
                <button
                  onClick={() => setActiveTab("preview")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "preview"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <FiMail size={16} />
                  Email Preview
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
              {activeTab === "raw" ? (
                <div className="space-y-4">
                  {/* Raw Subject */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Subject Line
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <code className="text-sm text-gray-800 font-mono break-words">
                        {subject || (
                          <span className="text-gray-400">
                            No subject yet...
                          </span>
                        )}
                      </code>
                    </div>
                  </div>

                  {/* Raw Body */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Email Body
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 min-h-[200px]">
                      <pre className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-words">
                        {body || (
                          <span className="text-gray-400">No body yet...</span>
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview Note */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      <strong>Note:</strong> Preview uses sample contact data to
                      show how your email will look.
                    </p>
                  </div>

                  {/* Email Preview */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                    {/* Email Header */}
                    <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                        <span className="font-semibold">To:</span>
                        <span>odental657@gmail.com</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600 font-normal">
                          Subject:
                        </span>
                        <span className="font-semibold text-gray-900">
                          {subject ? (
                            previewWithSampleData(subject)
                          ) : (
                            <span className="text-gray-400 font-normal">
                              No subject yet...
                            </span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="p-4 bg-white min-h-[200px]">
                      <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {body ? (
                          previewWithSampleData(body)
                        ) : (
                          <span className="text-gray-400">No body yet...</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sample Data Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">
                      Sample Data Used
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="ml-1 text-gray-800">John Smith</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Company:</span>
                        <span className="ml-1 text-gray-800">
                          Oris Dental Center
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Category:</span>
                        <span className="ml-1 text-gray-800">Dentists</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Area:</span>
                        <span className="ml-1 text-gray-800">Jumeirah</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
